/**
 * Comparar datos de identidad: dueña única de la regla (diseño 4a, §2.4; AGENTS.md, «Comparar datos de identidad»). Dos usos:
 * - registro frente a registro: «Comparar» del servicio (compare-view.ts), que pone lado a lado la ficha de la lista y las personas
 *   encontradas con el mismo nombre;
 * - la persona encontrada frente a tu cliente: la comparación de la ficha (app/shared/piezas/comparacion.ts la reexporta para la SPA).
 *
 * Vive en el servicio, y no en app/shared, porque el servicio corre en el VPS desde su propio paquete y no alcanza app/shared (como
 * relleno.ts). Sin dependencias. Nunca concluye: cuenta qué coincide, qué no y qué no se puede comparar; «es tu cliente» es de la
 * etapa 5. Los datos del cliente sólo viven en la memoria de la pantalla: nada de aquí los guarda ni los envía.
 *
 * Reglas:
 * - Sólo contradice lo comparable: dos fechas completas, dos RFC completos (13 caracteres de persona, 12 de empresa), dos CURP
 *   completas, o dos raíces de RFC. Un año suelto nunca contradice (OFAC escribe «circa 1980»): mismo año, coincide en parte; otro
 *   año, no se puede comparar.
 * - Un RFC completo sólo coincide del todo con otro completo igual. Con la misma raíz y distinta homoclave coincide en parte (el acta
 *   puede tener la homoclave mal asentada; decisión de Bernardo, 2026-10-04); si alguno no trae homoclave, se compara la raíz.
 * - Domicilio y lugar de nacimiento frente al cliente: ciudad y estado (de la lista de 32) contra los dos últimos segmentos del texto
 *   del acta, seguidos y en ese orden. Sólo «coincide» o «no se puede comparar»: nunca «no coincide».
 * - Domicilio registro contra registro: calle y número (la regla de «Comparar»), que tampoco contradice.
 */

/** Sin acentos, sin signos y en mayúsculas: dos escrituras de lo mismo comparan igual. */
export const normalizar = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

const MESES: Record<string, string> = { JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06', JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12' };

/**
 * Hasta dónde una fecha es fecha: un mes que no existe deja sólo el año («1985-00-00» → «1985», como escriben los extractores un mes y
 * un día que no se leyeron) y un día que no existe en ese mes deja el año y el mes («1985-02-30» → «1985-02»). 2026-10-08: antes pasaba
 * entera y, frente a la fecha del cliente, salía «no coincide» (bitácora 2026-10-08-senales-buscador).
 */
function fechaHasta(anio: string, mes?: string, dia?: string): string {
  const m = Number(mes);
  if (!mes || !(m >= 1 && m <= 12)) return anio;
  const d = Number(dia);
  if (!dia || !(d >= 1 && d <= new Date(Date.UTC(Number(anio), m, 0)).getUTCDate())) return `${anio}-${mes}`;
  return `${anio}-${mes}-${dia}`;
}

/**
 * Las fechas de relleno de los sistemas y las de fuera de 1900–2099 no dicen cuándo nació nadie: no coinciden ni contradicen, ni frente a
 * tu cliente ni entre registros (revisión del 2026-10-08: un «1900-01-01» del acta descartaba al cliente y bajaba el riesgo).
 */
const FECHAS_RELLENO = new Set(['1900-01-01', '1901-01-01', '1899-12-31', '1970-01-01', '2000-01-01', '1111-11-11', '9999-12-31', '9999-01-01']);
/** Una fecha de la fuente que sirve para comparar (AAAA, AAAA-MM o AAAA-MM-DD), o null: ni relleno, ni fuera de rango, ni leída sólo en parte. */
export function fechaUtil(crudo: string): string | null {
  const f = fechaIso(crudo);
  if (!f || FECHAS_RELLENO.has(f) || fechaLeidaEnParte(crudo)) return null;
  const anio = Number(f.slice(0, 4));
  return anio >= 1900 && anio <= 2099 ? f : null;
}
/** Los RFC genéricos del SAT (público en general, extranjeros): no dicen quién es nadie. */
export const rfcGenerico = (s: string): boolean => /^X[AE]XX010101/.test(s);
/** Un RFC de la fuente que sirve para comparar con el de una persona (o una empresa): con su forma y su fecha en el calendario, no genérico y del mismo tipo. */
export const rfcUtil = (s: string, persona: boolean): boolean => !rfcGenerico(s) && (persona ? rfcPersona(s) : rfcEmpresa(s));

/** Una fecha que traía día (o mes) y sólo se pudo leer en parte («1985-02-30» → «1985-02»): no dice «misma fecha» en «Comparar». */
export function fechaLeidaEnParte(crudo: string): boolean {
  const t = crudo.trim();
  // «1985-00-00» y «1985-02-00» son la forma de los extractores para «sólo el año» y «sólo el año y el mes» (revisión del 2026-10-08)
  if (/^\d{4}-00-00$/.test(t) || /^\d{4}-(0[1-9]|1[0-2])-00$/.test(t)) return false;
  const traia = /^\d{4}-\d{2}(-\d{2})?$/.test(t) ? t.length : /^\d{1,2} [A-Za-z]{3} \d{4}$/.test(t) ? 10 : 0;
  const iso = fechaIso(t);
  return traia > 0 && iso !== null && iso.length < traia;
}

/** Una fecha como `AAAA`, `AAAA-MM` o `AAAA-MM-DD` (OFAC escribe «11 Jul 1980» o «circa 1980»); si no se entiende, null y no se compara. */
export function fechaIso(crudo: string): string | null {
  const t = crudo.trim();
  const iso = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(t);
  if (iso) return fechaHasta(iso[1]!, iso[2], iso[3]);
  const m = /^(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/.exec(t);
  if (m && MESES[m[2]!.toUpperCase()]) return fechaHasta(m[3]!, MESES[m[2]!.toUpperCase()], m[1]!.padStart(2, '0'));
  const y = /^(\d{4})$/.exec(t.replace(/^circa\s+/i, ''));
  return y ? y[1]! : null;
}

/**
 * Cómo se relacionan dos valores: iguales completos, iguales sólo en la parte que ambos tienen, distintos de verdad (comparables),
 * o no comparables.
 */
export type Relacion = 'igual' | 'en_parte' | 'distinto' | 'no_comparable';

/**
 * El día y el mes al revés, el mismo año: «1988-12-03» y «1988-03-12» (dos fechas AAAA-MM-DD). Decisión de Bernardo del 2026-10-08:
 * no contradicen. Dueña única desde la revisión de la cuarta ronda (2026-10-08): la usan las señales del Buscador, comparar() frente a tu
 * cliente, «Comparar» (compare-view.ts) y «¿Es la misma persona?» (app/shared/piezas/misma.ts).
 */
export const invertidas = (x: string, y: string): boolean =>
  x.length === 10 && y.length === 10 && x !== y && x.slice(0, 4) === y.slice(0, 4) && x.slice(5, 7) === y.slice(8, 10) && x.slice(8, 10) === y.slice(5, 7);
/**
 * Dos fechas completas (AAAA-MM-DD, ya leídas con fechaUtil) que de verdad son distintas: ni el día y el mes al revés, ni el 1 de enero
 * frente a otra fecha del mismo año (varios sistemas ponen el 1 de enero cuando sólo saben el año). Revisión de la cuarta ronda
 * (2026-10-08): antes, frente a tu cliente, decían «no coincide» y podían bajar la ficha a «riesgo bajo».
 */
export function fechasDistintas(a: string, b: string): boolean {
  if (a.length !== 10 || b.length !== 10 || a === b || invertidas(a, b)) return false;
  return !(a.slice(0, 4) === b.slice(0, 4) && (a.endsWith('-01-01') || b.endsWith('-01-01')));
}

/** Fechas a la precisión menor; sólo dos fechas completas pueden ser distintas. */
export function relacionFechas(a: string, b: string): Relacion {
  if (!a || !b) return 'no_comparable';
  const n = Math.min(a.length, b.length);
  if (a.slice(0, n) === b.slice(0, n)) return a.length === 10 && b.length === 10 ? 'igual' : 'en_parte';
  return a.length === 10 && b.length === 10 ? 'distinto' : 'no_comparable';
}

/**
 * La fecha AAMMDD de un RFC o una CURP existe en el calendario: el mes de 01 a 12 y un día que ese mes tiene en 19AA o en 20AA («000229»
 * sí, por el 2000; «290229» no). Ronda final del 2026-10-08 (Astra 1): con la forma correcta y la fecha imposible («PELJ881332AB1», mes 13
 * y día 32), una clave es inútil: ni coincide, ni contradice, ni descarta al cliente (propiedad 7 de la tabla de las señales).
 */
export function fechaClaveValida(aammdd: string): boolean {
  if (!/^\d{6}$/.test(aammdd)) return false;
  const aa = Number(aammdd.slice(0, 2)), m = Number(aammdd.slice(2, 4)), d = Number(aammdd.slice(4, 6));
  if (m < 1 || m > 12 || d < 1) return false;
  return [1900 + aa, 2000 + aa].some((a) => d <= new Date(Date.UTC(a, m, 0)).getUTCDate());
}
/** Forma de un RFC: persona, 4 letras y 6 dígitos; empresa, 3 letras y 6 dígitos; con o sin los 3 de la homoclave. Con su fecha en el calendario. */
const RFC_PERSONA = /^[A-ZÑ&]{4}\d{6}([A-Z0-9]{3})?$/;
const RFC_EMPRESA = /^[A-ZÑ&]{3}\d{6}([A-Z0-9]{3})?$/;
const rfcPersona = (s: string): boolean => RFC_PERSONA.test(s) && fechaClaveValida(s.slice(4, 10));
const rfcEmpresa = (s: string): boolean => RFC_EMPRESA.test(s) && fechaClaveValida(s.slice(3, 9));
export const rfcConForma = (s: string): boolean => rfcPersona(s) || rfcEmpresa(s);
/** RFC completo: 13 caracteres de persona, 12 de empresa, con la forma de un RFC. */
export const rfcCompleto = (s: string): boolean => rfcConForma(s) && (s.length === 13 || s.length === 12);
/** Raíz del RFC, sin homoclave: 10 caracteres de persona, 9 de empresa. */
export const raizRfc = (s: string): string => (!rfcConForma(s) ? s : s.length === 13 ? s.slice(0, 10) : s.length === 12 ? s.slice(0, 9) : s);

/** Dos RFC completos con la misma raíz y distinta homoclave: el acta puede tenerla mal asentada (`consolidate.py`, 763-767). */
export const homoclaveDistinta = (a: string, b: string): boolean => rfcCompleto(a) && rfcCompleto(b) && a !== b && raizRfc(a) === raizRfc(b);

/** Dos RFC completos coinciden sólo si son iguales; con la misma raíz, o si alguno no trae homoclave, a lo más coinciden en parte. */
export function relacionRfc(a: string, b: string): Relacion {
  // Lo que no tiene forma de RFC no se compara: no puede contradecir ni coincidir (ni siquiera consigo mismo).
  if (!rfcConForma(a) || !rfcConForma(b)) return 'no_comparable';
  if (a === b) return rfcCompleto(a) ? 'igual' : 'en_parte';
  // Dos completos: iguales, o la misma raíz con distinta homoclave (en parte, decisión de Bernardo del 2026-10-04), o distintos.
  if (rfcCompleto(a) && rfcCompleto(b)) return homoclaveDistinta(a, b) ? 'en_parte' : 'distinto';
  const ra = raizRfc(a), rb = raizRfc(b);
  return ra === rb ? 'en_parte' : 'distinto';
}

/** La forma de una CURP: 4 letras, 6 dígitos, el sexo (H, M o X), 5 letras, una letra o un dígito y un dígito; su fecha, en el calendario. */
const CURP_FORMA = /^[A-Z]{4}\d{6}[HMX][A-Z]{5}[A-Z0-9]\d$/;
export const curpConForma = (s: string): boolean => CURP_FORMA.test(s) && fechaClaveValida(s.slice(4, 10));
/** Una clave con los 6 dígitos de la fecha donde van y una fecha que no existe: inútil, ni siquiera coincide consigo misma (ronda final). */
const fechaImposible = (s: string): boolean => /^.{4}\d{6}/.test(s) && !fechaClaveValida(s.slice(4, 10));
/**
 * Dos CURP completas: iguales o distintas. Una incompleta no contradice. Lo que no tiene forma de CURP (basura de la lectura con 18
 * caracteres: una «O» donde va un cero) tampoco: generador con oráculo del 2026-10-08, frente a tu cliente descartaba al grupo y bajaba
 * la ficha a «riesgo bajo». Con la forma y una fecha que no existe en el calendario, tampoco coincide (ronda final del 2026-10-08).
 */
export function relacionCurp(a: string, b: string): Relacion {
  if (a.length === 18 && b.length === 18) {
    if (fechaImposible(a) || fechaImposible(b)) return 'no_comparable';
    return a === b ? 'igual' : curpConForma(a) && curpConForma(b) ? 'distinto' : 'no_comparable';
  }
  return a && a === b ? 'en_parte' : 'no_comparable';
}

/** Un domicilio reducido a su primera parte (calle y número): coinciden si comparten el número y una palabra de la calle. */
function calle(s: string) {
  const palabras = normalizar(s.split(',')[0] ?? '').split(' ');
  return {
    numeros: new Set(palabras.filter((w) => /^\d{1,4}$/.test(w))),
    palabras: new Set(palabras.filter((w) => /^[A-Z]{4,}$/.test(w) && !['CALLE', 'NUMERO', 'INTERIOR', 'AVENIDA', 'COLONIA', 'INTER'].includes(w))),
  };
}

/** Domicilio registro contra registro («Comparar»): misma calle y número. Nunca contradice. */
export function relacionCalle(a: string, b: string): Relacion {
  const x = calle(a), y = calle(b);
  return [...x.numeros].some((n) => y.numeros.has(n)) && [...x.palabras].some((w) => y.palabras.has(w)) ? 'igual' : 'no_comparable';
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Las señales del Buscador (buscador-senales.ts): más estrictas que «Comparar», porque escriben una frase («tiene el mismo domicilio»,
// «nacieron en el mismo lugar»). 2026-10-08, con las revisiones adversariales de esa fecha (bitácora 2026-10-08-senales-buscador).

const GENERICAS_CALLE = new Set(['CALLE', 'C', 'AV', 'AVE', 'AVENIDA', 'BLVD', 'BOULEVARD', 'BULEVAR', 'CALZADA', 'CALZ', 'PRIVADA', 'PRIV', 'CERRADA',
  'CDA', 'ANDADOR', 'AND', 'PROLONGACION', 'PROL', 'CIRCUITO', 'CTO', 'RETORNO', 'RET', 'PASEO', 'CAMINO', 'CARRETERA', 'CARR', 'NO', 'NUM', 'NUMERO',
  'N', 'EXT', 'EXTERIOR', 'OFICIAL']);
/** Las marcas de un interior, con su forma única: «EDIFICIO B DEPTO 3» no es «EDIFICIO C DEPTO 3», ni «DEPTO 3» es «LOCAL 3». */
const MARCA_INTERIOR: Record<string, string> = { INT: 'INT', INTERIOR: 'INT', DEPTO: 'DEPTO', DEPARTAMENTO: 'DEPTO', DPTO: 'DEPTO', LOCAL: 'LOCAL', PISO: 'PISO',
  OFICINA: 'OF', OF: 'OF', EDIFICIO: 'EDIF', EDIF: 'EDIF', TORRE: 'TORRE', CASA: 'CASA' };
/** Lo que no nombra una calle: «CALLE SIN NOMBRE», «CALLE PRINCIPAL», un lote o una manzana sin calle, «DOMICILIO CONOCIDO». */
const CALLE_NO_IDENTIFICA = new Set(['SIN', 'NOMBRE', 'PRINCIPAL', 'LOTE', 'LT', 'MZ', 'MZA', 'MANZANA', 'DOMICILIO', 'CONOCIDO', 'CONOSIDO', 'SN']);
/**
 * La primera parte de un domicilio leída como calle, número exterior e interior. Se quita antes el código postal («C.P. 80000»): no es un
 * número de casa. Con «S/N» no hay número. Un número con su letra («12-A», «12 A») es un número («12A»). El exterior es el último número
 * que no es el valor de una marca de interior; el interior, cada marca con su valor. La calle es lo que va antes, sin las palabras
 * genéricas («CALLE», «AV.», «NO.»); si no tiene una palabra que la nombre («SIN NOMBRE», «PRINCIPAL», «LOTE 3», «DOMICILIO CONOCIDO»),
 * no se lee. Revisiones del 2026-10-08.
 */
function calleNumero(s: string): { calle: string; exterior: string; interior: string | null } | null {
  const primera = normalizar((s.split(',')[0] ?? '').replace(/C\.?\s?P\.?\s*\d{5}/gi, ' ').replace(/c[oó]digo postal\s*\d{5}/gi, ' '))
    .replace(/\b\d{5}\b/g, ' ');
  const t0 = primera.split(' ').filter(Boolean);
  if (t0.some((w, k) => (w === 'S' && t0[k + 1] === 'N') || w === 'SN' || (w === 'SIN' && t0[k + 1] === 'NUMERO'))) return null;
  // el número con su letra
  const t: string[] = [];
  for (const w of t0) { if (/^[A-Z]$/.test(w) && t.length && /^\d{1,4}$/.test(t.at(-1)!) && !MARCA_INTERIOR[t.at(-2) ?? '']) t[t.length - 1] += w; else t.push(w); }
  let exterior = -1;
  for (let k = 0; k < t.length; k++) {
    if (MARCA_INTERIOR[t[k]!] && k + 1 < t.length) { k++; continue; }
    if (/^\d{1,4}[A-Z]?$/.test(t[k]!) && k > 0) exterior = k;
  }
  if (exterior < 0) return null;
  // el interior: cada marca con su valor, en cualquier tramo del domicilio («JUAREZ 12, INT 1, …»; letras incluidas: «INT A»)
  const todo = normalizar(s.replace(/C\.?\s?P\.?\s*\d{5}/gi, ' ')).split(' ').filter(Boolean);
  const interiores: string[] = [];
  for (let k = 0; k + 1 < todo.length; k++) if (MARCA_INTERIOR[todo[k]!]) { interiores.push(`${MARCA_INTERIOR[todo[k]!]} ${todo[k + 1]}`); k++; }
  const calle = t.slice(0, exterior).filter(w => !GENERICAS_CALLE.has(w) && !MARCA_INTERIOR[w]);
  if (!calle.some(w => /^[A-Z]{3,}$/.test(w) && !CALLE_NO_IDENTIFICA.has(w)) || calle.includes('CONOCIDO') || calle.includes('CONOSIDO')) return null;
  return { calle: calle.join(' '), exterior: t[exterior]!, interior: interiores.length ? interiores.sort().join(' ') : null };
}

/** La colonia (o fraccionamiento, barrio) de un domicilio, si la dice: hasta el código postal o la coma siguiente. */
function colonia(s: string): string | null {
  for (const seg of s.split(',')) {
    const m = /\b(COL|COLONIA|FRACC|FRACCIONAMIENTO|BARRIO)\b (.+)$/.exec(normalizar(seg));
    if (m) return m[2]!.replace(/\b(C ?P|CODIGO POSTAL)\b.*$/, '').replace(/\b\d{5}\b.*$/, '').trim() || null;
  }
  return null;
}
/** Un segmento que no puede ser la ciudad: una colonia, un código postal o un número. */
const NO_ES_CIUDAD = /^(COL|COLONIA|FRACC|FRACCIONAMIENTO|BARRIO|C ?P|CODIGO POSTAL|\d)/;
/** La ciudad y el estado de un domicilio: los dos últimos tramos, si la ciudad no es la calle (el primer tramo) ni una colonia. */
function ciudadDomicilio(s: string): { ciudad: string; estado: Estado } | null {
  const sinCp = s.replace(/C\.?\s?P\.?\s*\d{5}/gi, ' ').replace(/\b\d{5}\b/g, ' ');
  const segmentos = sinCp.split(',').map((x) => x.trim()).filter((x) => normalizar(x) !== '');
  while (segmentos.length && ['MEXICO', 'REPUBLICA MEXICANA'].includes(normalizar(segmentos.at(-1)!))) segmentos.pop();
  if (segmentos.length < 3) return null;
  const c = lugarDelActa(segmentos.join(', '));
  return c && !NO_ES_CIUDAD.test(normalizar(segmentos.at(-2)!)) ? c : null;
}
const titulo = (s: string) => s.toLowerCase().replace(/(^|\s)(\p{L})/gu, (_m, a: string, l: string) => a + l.toUpperCase()).replace(/\b(De|Del|La|Las|Los|Y)\b/g, w => w.toLowerCase());

/**
 * El domicilio que comparten dos textos, escrito sólo con lo que dicen los dos (calle, número, el interior y la colonia si los dos los
 * dicen, ciudad y estado), o null si no es el mismo: otra calle, otro número, otro interior, otra colonia, o sin ciudad y estado
 * legibles en los dos («Juárez 12» y «Juárez 99», «S/N», un código postal, «CALLE SIN NOMBRE 1», la calle tomada por ciudad).
 */
export function domicilioComun(a: string, b: string): string | null {
  const x = calleNumero(a), y = calleNumero(b);
  // el mismo interior, o ninguno en los dos: con el interior en un solo lado sólo se sabe que es el mismo edificio (revisión de Sol)
  if (!x || !y || x.calle !== y.calle || x.exterior !== y.exterior || x.interior !== y.interior) return null;
  const kx = colonia(a), ky = colonia(b);
  if (kx !== null && ky !== null && kx !== ky) return null;
  const cx = ciudadDomicilio(a), cy = ciudadDomicilio(b);
  if (!cx || !cy || cx.ciudad !== cy.ciudad || cx.estado !== cy.estado) return null;
  return [`${titulo(x.calle)} ${x.exterior}${x.interior !== null ? ` ${titulo(x.interior)}` : ''}`,
    ...(kx !== null && ky !== null ? [`Col. ${titulo(kx)}`] : []), titulo(cx.ciudad), cx.estado].join(', ');
}
export const mismoDomicilio = (a: string, b: string): boolean => domicilioComun(a, b) !== null;

const PAIS = new Set(['MEXICO', 'REPUBLICA MEXICANA', 'ESTADOS UNIDOS MEXICANOS']);
/** Países: solos no dicen un lugar de nacimiento. */
// en minúsculas en el código (la prueba del texto visible lee las mayúsculas como claves de programa); se comparan en mayúsculas
const PAISES = new Set([...PAIS, ...['mexicana', 'mexicano', 'estados unidos', 'estados unidos de america', 'eua', 'eeuu', 'ee uu', 'usa', 'us', 'guatemala', 'colombia', 'venezuela', 'espana', 'china', 'canada', 'honduras', 'el salvador', 'peru', 'argentina', 'chile', 'cuba', 'ecuador', 'nicaragua', 'costa rica', 'panama', 'bolivia', 'brasil', 'brazil', 'italia', 'francia', 'alemania', 'libano', 'israel', 'japon', 'corea', 'india', 'rusia'].map(x => x.toUpperCase())]);
const RELLENO_LUGAR = /^(NO |SIN |NULL$|IDEM$|EL MISMO|LA MISMA|ESTE LUGAR|ESTA LOCALIDAD|(EN )?ESTA CIUDAD|SE DESCONOCE|SE IGNORA|DESCONOCID|NINGUN|ILEGIBLE|LUGAR CONOCIDO|NACIONALIDAD|MEXICAN[OA]|S ?D$|N ?D$|N ?A$|S ?N$|[X ]+$|[\d ]+$)/;
/** Estados de otros países: solos no dicen una ciudad. */
const ESTADOS_EXTRANJEROS = new Set(['california', 'texas', 'arizona', 'florida', 'nevada', 'nuevo mexico', 'new mexico', 'illinois', 'nueva york', 'new york',
  'colorado', 'georgia', 'washington', 'oregon', 'utah', 'oklahoma', 'luisiana', 'louisiana', 'antioquia', 'valle del cauca', 'cundinamarca'].map(x => x.toUpperCase()));
const compactoPais = (s: string) => normalizar(s).replace(/ /g, '');
const PAISES_COMPACTOS = new Set([...PAISES].map(compactoPais));
/** Los prefijos que no son parte del nombre del lugar. */
const sinPrefijo = (seg: string) => seg.replace(/^\s*(nacid[oa] en|naci[oó] en|originari[oa] de|municipio de|mpio\.? de|en)\s+/i, '');
/** El estado de un segmento, también con «Estado de» o «Edo. de» delante («ESTADO DE SINALOA»); «Estado de México» sigue siendo ese estado. */
function estadoConPrefijo(seg: string): Estado | null {
  return estadoDe(seg) ?? estadoDe(seg.replace(/^\s*(estado|edo\.?)\s+(de\s+)?/i, ''));
}
/**
 * Un lugar de nacimiento como ciudad y estado. Se quita el país del final («Culiacan, Sinaloa, Mexico», como escribe OFAC; «Guadalajara,
 * Jal., Mex.»: «MEX» es el país si antes viene un estado) y los prefijos («Nacido en», «Municipio de»). Con el estado al final: {ciudad
 * del segmento anterior, estado}; la Ciudad de México con su alcaldía si la dice. «… (ESTA CIUDAD)»: lo de antes, y si es un estado, la
 * ciudad que se llama como él. Sin estado mexicano: {el texto, sin estado}, salvo un país o un estado de otro país. null si no dice un
 * lugar: un relleno, una nacionalidad, un país o un estado solo («Guerrero», «Texas», «California, USA»), o una colonia.
 */
export function lugarNacimiento(texto: string): { ciudad: string; estado: Estado | null; localidad?: string } | null {
  let t = texto.trim();
  const estaCiudad = /^(.+?)\s*\(?\s*(en\s+)?esta ciudad\s*\)?\s*$/i.exec(t);
  if (estaCiudad) {
    t = estaCiudad[1]!.trim();
    if (/^(EN)?$/.test(normalizar(t))) return null;
    if (!t.includes(',') && !RELLENO_LUGAR.test(normalizar(t))) {
      const e = estadoConPrefijo(t);
      if (e) return e === 'Ciudad de México' ? { ciudad: '', estado: e } : { ciudad: ciudad(t), estado: e };
    }
  }
  const segmentos = t.split(',').map(s => sinPrefijo(s.trim())).filter(s => normalizar(s) !== '');
  if (segmentos.length === 0 || RELLENO_LUGAR.test(normalizar(segmentos.join(' ')))) return null;
  // el país del final: «MEXICO» (o «MEX» si antes viene un estado), y uno extranjero deja un lugar de otro país
  let extranjero = false;
  for (;;) {
    const ultimo = segmentos.at(-1)!, antes = segmentos.at(-2);
    // «MEXICO» al final es el país con tres tramos o si antes viene un estado («Mexico City, Mexico»); «Toluca, Mexico» no se toca
    if (segmentos.length >= 2 && ((PAIS.has(normalizar(ultimo)) && (segmentos.length >= 3 || !!estadoConPrefijo(antes!))) || (normalizar(ultimo) === 'MEX' && !!antes && !!estadoConPrefijo(antes)))) { segmentos.pop(); continue; }
    if (segmentos.length >= 1 && PAISES_COMPACTOS.has(compactoPais(ultimo)) && !PAIS.has(normalizar(ultimo))) { segmentos.pop(); extranjero = true; continue; }
    break;
  }
  // sólo el país («MEXICO, MEXICO», «Mexico»), o un estado de otro país, no dice un lugar
  if (segmentos.length === 0 || segmentos.every(x => PAIS.has(normalizar(x)))) return null;
  if (segmentos.length === 1 && ESTADOS_EXTRANJEROS.has(normalizar(segmentos[0]!))) return null;
  if (extranjero) {
    // de otro país: sólo con ciudad y región («Medellin, Antioquia, Colombia»); una región sola no dice una ciudad
    return segmentos.length >= 2 && !ESTADOS_EXTRANJEROS.has(normalizar(segmentos[0]!)) ? { ciudad: ciudad(segmentos.join(' ')), estado: null } : null;
  }
  const estado = estadoConPrefijo(segmentos.at(-1)!);
  // la Ciudad de México: la alcaldía, si la dice («COYOACAN, D.F.»); «MEXICO» o «Ciudad de México» antes no es una alcaldía
  if (estado === 'Ciudad de México') {
    const antes = segmentos.length >= 2 ? ciudad(segmentos.at(-2)!) : '';
    return { ciudad: ['', 'MEXICO', 'CIUDAD DE MEXICO'].includes(antes) || NO_ES_CIUDAD.test(antes) ? '' : antes, estado };
  }
  if (estado) {
    if (segmentos.length < 2) return null;
    const c = ciudad(segmentos.at(-2)!);
    // lo que va antes de la ciudad es una localidad dentro de ella («EL ROSARIO, CULIACAN, SINALOA»)
    const localidad = segmentos.length >= 3 ? ciudad(segmentos.slice(0, -2).join(' ')) : '';
    return NO_ES_CIUDAD.test(c) ? null : { ciudad: c, estado, ...(localidad ? { localidad } : {}) };
  }
  const c = ciudad(segmentos.join(' '));
  return NO_ES_CIUDAD.test(c) ? null : { ciudad: c, estado: null };
}

/**
 * El lugar de nacimiento que comparten dos textos, escrito sólo con lo que dicen los dos («Tlalpan, D.F.» y «Distrito Federal»: «Ciudad
 * de México»), o null si no es el mismo: la misma ciudad y el mismo estado, o el mismo texto si ninguno dice estado; si sólo uno lo dice,
 * no se sabe cuál es («San Pedro» frente a «San Pedro, Coahuila»). Nunca un prefijo.
 */
export function lugarComun(a: string, b: string): string | null {
  const x = lugarNacimiento(a), y = lugarNacimiento(b);
  if (!x || !y) return null;
  if (x.estado === 'Ciudad de México' || y.estado === 'Ciudad de México') {
    if (x.estado !== y.estado) return null;
    if (x.ciudad !== '' && y.ciudad !== '' && x.ciudad !== y.ciudad) return null;
    return x.ciudad !== '' && x.ciudad === y.ciudad ? `${titulo(x.ciudad)}, Ciudad de México` : 'Ciudad de México';
  }
  if (x.ciudad === '' || x.ciudad !== y.ciudad || x.estado !== y.estado) return null;
  // dos localidades distintas de la misma ciudad no son el mismo lugar; la localidad se escribe sólo si los dos la dicen (revisión de Astra)
  if (x.localidad && y.localidad && x.localidad !== y.localidad) return null;
  const loc = x.localidad && x.localidad === y.localidad ? `${titulo(x.localidad)}, ` : '';
  return x.estado ? `${loc}${titulo(x.ciudad)}, ${x.estado}` : titulo(x.ciudad);
}
export const mismoLugarNacimiento = (a: string, b: string): boolean => lugarComun(a, b) !== null;

/** Lugar de nacimiento registro contra registro («Comparar»): el mismo texto, o uno que empieza con el otro. Nunca contradice. */
export function relacionLugarRegistros(a: string, b: string): Relacion {
  const x = normalizar(a), y = normalizar(b);
  return x && y && (x === y || x.startsWith(`${y} `) || y.startsWith(`${x} `)) ? 'igual' : 'no_comparable';
}

// ---------------------------------------------------------------------------------------------------------------------------------
// La persona encontrada frente a tu cliente.

/** Los 32 estados, como los elige el analista. */
export const ESTADOS = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima',
  'Durango', 'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
  'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán',
  'Zacatecas',
] as const;
export type Estado = (typeof ESTADOS)[number];

/** Cómo escriben las actas cada estado (sólo letras, sin acentos), además de su nombre. Lista cerrada. */
const ABREVIATURAS: Record<Estado, string[]> = {
  'Aguascalientes': ['AGS'],
  'Baja California': ['BC', 'BCN', 'BAJACALIFORNIANORTE'],
  'Baja California Sur': ['BCS'],
  'Campeche': ['CAMP', 'CAM'],
  'Chiapas': ['CHIS'],
  'Chihuahua': ['CHIH'],
  'Ciudad de México': ['CDMX', 'DF', 'DISTRITOFEDERAL', 'MEXICODF', 'CDDEMEXICO', 'MEXICOCITY', 'MEXICODISTRITOFEDERAL', 'CIUDADDEMEXICODISTRITOFEDERAL'],
  'Coahuila': ['COAH', 'COAHUILADEZARAGOZA'],
  'Colima': ['COL'],
  'Durango': ['DGO'],
  'Estado de México': ['MEX', 'EDOMEX', 'EDODEMEX', 'EDODEMEXICO', 'ESTADODEMEX', 'EDOMEXICO'],
  'Guanajuato': ['GTO'],
  'Guerrero': ['GRO'],
  'Hidalgo': ['HGO'],
  'Jalisco': ['JAL'],
  'Michoacán': ['MICH', 'MICHOACANDEOCAMPO'],
  'Morelos': ['MOR'],
  'Nayarit': ['NAY'],
  'Nuevo León': ['NL'],
  'Oaxaca': ['OAX'],
  'Puebla': ['PUE'],
  'Querétaro': ['QRO', 'QUERETARODEARTEAGA'],
  'Quintana Roo': ['QROO', 'QR'],
  'San Luis Potosí': ['SLP'],
  'Sinaloa': ['SIN'],
  'Sonora': ['SON'],
  'Tabasco': ['TAB'],
  'Tamaulipas': ['TAMPS', 'TAMS'],
  'Tlaxcala': ['TLAX'],
  'Veracruz': ['VER', 'VERACRUZDEIGNACIODELALLAVE'],
  'Yucatán': ['YUC'],
  'Zacatecas': ['ZAC'],
};
const compacto = (s: string) => normalizar(s).replace(/ /g, '');
const POR_ESCRITURA = new Map<string, Estado>();
for (const e of ESTADOS) for (const k of [compacto(e), ...ABREVIATURAS[e]]) POR_ESCRITURA.set(k, e);

/** El estado de una escritura del acta («SIN.», «N.L.», «Edo. Méx.», «CDMX»…), o null si no se reconoce. */
export function estadoDe(texto: string): Estado | null {
  return POR_ESCRITURA.get(compacto(texto)) ?? null;
}

/** Una ciudad normalizada: sin acentos ni signos, «Cd.» como «Ciudad». */
function ciudad(s: string): string {
  return normalizar(s.replace(/\bCd\.?(?=\s|$)/gi, 'Ciudad '));
}

/** Ciudad y estado al final del texto del acta (sus dos últimos segmentos), o null si no se pueden leer. */
export function lugarDelActa(texto: string): { ciudad: string; estado: Estado } | null {
  const sinCp = texto.replace(/C\.?\s?P\.?\s*\d{5}/gi, ' ').replace(/\b\d{5}\b/g, ' ');
  const segmentos = sinCp.split(',').map((s) => s.trim()).filter((s) => normalizar(s) !== '');
  while (segmentos.length && ['MEXICO', 'REPUBLICA MEXICANA'].includes(normalizar(segmentos.at(-1)!))) segmentos.pop();
  if (segmentos.length < 2) return null;
  const estado = estadoDe(segmentos.at(-1)!);
  const c = ciudad(segmentos.at(-2)!);
  return estado && c ? { ciudad: c, estado } : null;
}

export interface Lugar {
  ciudad: string;
  /** null: tu cliente dio la ciudad pero no el estado (un lugar a medias no se compara: «no se puede comparar», nunca «no lo dio»). */
  estado: Estado | null;
}

/** Ciudad y estado del cliente contra el texto del acta: coincide o no se puede comparar; nunca «no coincide». */
export function relacionLugar(cliente: Lugar, textoActa: string): Relacion {
  const acta = lugarDelActa(textoActa);
  const c = ciudad(cliente.ciudad);
  if (!acta || !c || !cliente.estado) return 'no_comparable';
  return acta.ciudad === c && acta.estado === cliente.estado ? 'igual' : 'no_comparable';
}

/** Lo que el analista escribe en el buscador; sólo en memoria. */
export interface DatosCliente {
  /** AAAA-MM-DD, de <input type="date">: nacimiento de una persona, constitución de una empresa. */
  fecha?: string;
  domicilio?: Lugar;
  nacimiento?: Lugar;
  rfcOCurp?: string;
}

export type EstadoDato = 'coincide' | 'coincide_en_parte' | 'no_coincide' | 'no_comparable' | 'falta_cliente' | 'falta_encontrado';
export type CampoComparado = 'fecha' | 'domicilio' | 'lugar_nacimiento' | 'rfc' | 'curp';

export interface ValorEncontrado<F> {
  valor: string;
  fuente: F;
  estado: EstadoDato;
  parte?: 'anio' | 'mes' | 'raiz_rfc' | 'homoclave_distinta';
}

export interface FilaComparacion<F> {
  campo: CampoComparado;
  cliente: string | null;
  encontrados: ValorEncontrado<F>[];
  /** El resumen de la fila: el peor caso que se puede decir sin concluir. */
  estado: EstadoDato;
  /** Una de sus actas coincide y otra no (texto 7d). */
  mixto: boolean;
}

export interface Comparacion<F> {
  filas: FilaComparacion<F>[];
  /** Filas que coinciden completas ([n] de «Coinciden [n] de [m] datos»). */
  coinciden: number;
  /** Filas que coinciden sólo en parte (se dicen aparte, texto 5b). */
  enParte: number;
  /** [m]: campos del tipo de sujeto con valor en al menos uno de los dos lados. */
  comparados: number;
  /** Campos con valor de un solo lado. */
  faltan: CampoComparado[];
}

/** Lo que la comparación necesita de `sujeto` (argus.hallazgos/1). */
export interface SujetoComparable<F> {
  tipo: 'persona' | 'empresa';
  datos: readonly { campo: string; valor: string; fuente: F }[];
}

const DE_RELACION: Record<Relacion, EstadoDato> = { igual: 'coincide', en_parte: 'coincide_en_parte', distinto: 'no_coincide', no_comparable: 'no_comparable' };
const PEOR: EstadoDato[] = ['no_coincide', 'no_comparable', 'coincide_en_parte', 'coincide'];
/** Una clave (RFC o CURP) en mayúsculas, sin espacios ni signos: como se compara. */
export const limpiaClave = (s: string) => s.toUpperCase().replace(/[^A-Z0-9Ñ&]/g, '');

/** Compara los datos de la persona o empresa encontrada con los de tu cliente, valor por valor. */
export function comparar<F>(sujeto: SujetoComparable<F>, cliente: DatosCliente): Comparacion<F> {
  const persona = sujeto.tipo === 'persona';
  const valores = (campo: string) => sujeto.datos.filter((d) => d.campo === campo && d.valor.trim() !== '');
  const clave = cliente.rfcOCurp ? limpiaClave(cliente.rfcOCurp) : '';
  const esCurp = clave.length === 18;

  const fila = (campo: CampoComparado, deCliente: string | null, datos: readonly { valor: string; fuente: F }[], rel: (v: string) => [Relacion, ValorEncontrado<F>['parte']?]): FilaComparacion<F> => {
    // Sin dato de la persona encontrada no hay nada que comparar, lo haya dado o no tu cliente.
    if (datos.length === 0) return { campo, cliente: deCliente, encontrados: [], estado: 'falta_encontrado', mixto: false };
    if (deCliente === null) {
      return { campo, cliente: null, encontrados: datos.map((d) => ({ valor: d.valor, fuente: d.fuente, estado: 'falta_cliente' })), estado: 'falta_cliente', mixto: false };
    }
    const encontrados = datos.map((d): ValorEncontrado<F> => {
      const [r, parte] = rel(d.valor);
      const estado = DE_RELACION[r];
      return estado === 'coincide_en_parte' && parte ? { valor: d.valor, fuente: d.fuente, estado, parte } : { valor: d.valor, fuente: d.fuente, estado };
    });
    const estado = PEOR.find((e) => encontrados.some((v) => v.estado === e))!;
    const mixto = encontrados.some((v) => v.estado === 'no_coincide') && encontrados.some((v) => v.estado === 'coincide' || v.estado === 'coincide_en_parte');
    return { campo, cliente: deCliente, encontrados, estado, mixto };
  };

  const filas: FilaComparacion<F>[] = [];
  // Fecha: nacimiento de una persona, constitución de una empresa.
  // la fecha del cliente, sólo si existe en el calendario (la pantalla la valida; la API también llega aquí) y su año va de 1900 a 2099,
  // sin el «1900-01-01» de relleno de los sistemas (revisión de la cuarta ronda, 2026-10-08: descartaba al grupo y bajaba la ficha a
  // «riesgo bajo»). La lista de rellenos de las fuentes no se aplica: el banco da la fecha real de su cliente, y hay quien nació el
  // 1 de enero de 1970 o de 2000.
  const anioCliente = cliente.fecha ? Number(cliente.fecha.slice(0, 4)) : 0;
  const fechaCliente = cliente.fecha && /^\d{4}-\d{2}-\d{2}$/.test(cliente.fecha) && fechaIso(cliente.fecha) === cliente.fecha
    && anioCliente >= 1900 && anioCliente <= 2099 && cliente.fecha !== '1900-01-01' ? cliente.fecha : null;
  filas.push(fila('fecha', fechaCliente, valores(persona ? 'nacimiento' : 'constitucion'), (v) => {
    const iso = fechaUtil(v);
    if (!iso) {
      // una fecha de relleno igual a la del cliente coincide; distinta no se compara (nunca descarta)
      const cruda = fechaLeidaEnParte(v) ? null : fechaIso(v);
      return [cruda !== null && cruda === fechaCliente ? 'igual' : 'no_comparable'];
    }
    const r = relacionFechas(fechaCliente!, iso);
    // el día y el mes al revés, o el 1 de enero frente a otra fecha del mismo año, no se pueden comparar: nunca «no coincide» (revisión
    // de la cuarta ronda, 2026-10-08)
    if (r === 'distinto' && !fechasDistintas(fechaCliente!, iso)) return ['no_comparable'];
    return [r, iso.length === 4 ? 'anio' : 'mes'];
  }));
  // Un lugar cuenta como dado si trae la ciudad o el estado; a medias se compara como «no se puede comparar» (diseño 4a §2.4, paso 4).
  const textoLugar = (l?: Lugar) => (l && (l.ciudad.trim() || l.estado) ? [l.ciudad.trim(), l.estado].filter(Boolean).join(', ') : null);
  filas.push(fila('domicilio', textoLugar(cliente.domicilio), valores('domicilio'), (v) => [relacionLugar(cliente.domicilio!, v)]));
  if (persona) filas.push(fila('lugar_nacimiento', textoLugar(cliente.nacimiento), valores('lugar_nacimiento'), (v) => [relacionLugar(cliente.nacimiento!, v)]));
  // RFC o CURP: un solo campo del cliente; 18 caracteres es una CURP.
  filas.push(fila('rfc', clave && !esCurp ? clave : null, valores('rfc'), (v) => {
    const r = limpiaClave(v);
    // un RFC genérico o de otro tipo (el de una empresa en el registro de una persona), de la fuente o del cliente, no se compara (el del
    // cliente, desde la revisión de la cuarta ronda del 2026-10-08: su RFC de empresa frente a una persona decía «no coincide»)
    if (!rfcUtil(r, persona) || !rfcUtil(clave, persona)) return ['no_comparable'];
    return [relacionRfc(clave, r), homoclaveDistinta(clave, r) ? 'homoclave_distinta' : 'raiz_rfc'];
  }));
  if (persona) {
    filas.push(fila('curp', esCurp ? clave : null, valores('curp'), (v) => {
      const r = relacionCurp(clave, limpiaClave(v));
      // Una CURP incompleta igual no es una coincidencia: no hay «en parte» para la CURP.
      return [r === 'en_parte' ? 'no_comparable' : r];
    }));
  }

  const conValor = filas.filter((f) => f.cliente !== null || f.encontrados.length > 0);
  return {
    filas,
    coinciden: filas.filter((f) => f.estado === 'coincide').length,
    enParte: filas.filter((f) => f.estado === 'coincide_en_parte').length,
    comparados: conValor.length,
    faltan: conValor.filter((f) => f.estado === 'falta_cliente' || f.estado === 'falta_encontrado').map((f) => f.campo),
  };
}
